import React, { useState, useEffect, createContext, useContext, FC, ReactNode, MouseEvent, FormEvent, useRef, useCallback } from 'react';
import { ArrowRight, Cpu, Palette, CheckCircle, AlertTriangle, Wifi, WifiOff, User, PlusCircle, Shield, LayoutDashboard, Trash2, Download, Edit, Sparkles, FileCode, Save, ArrowLeft, Code, Brush, Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Importations pour l'éditeur de code
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism-tomorrow.css';

// --- Configuration ---
const APP_NAME = "SiteGenius AI";
const PRICE = "$19.99/mois";
const API_BASE_URL = "https://api.dauvin.dev"; // Modifiez si votre API est ailleurs

// --- Type Definitions ---
interface SiteFile { name: string; content: string; }
interface SiteContent { files: SiteFile[]; }
interface Site { id: string; name: string; content: SiteContent | string; createdAt: string; updatedAt: string; userId: string; }
interface UserData { id: string; email: string; role: 'USER' | 'ADMIN'; }
interface AdminStats { userCount: number; siteCount: number; subscriptionCount: number; }
interface AdminUser extends UserData { _count: { sites: number; }; stripeSubscriptionId: string | null; createdAt: string; }
interface OnboardingStep {
    id: 'industry' | 'style' | 'pages' | 'name' | 'description';
    question: string;
    placeholder?: string;
    options?: string[];
    templates?: { name: string, description: string }[];
    type: 'text' | 'textarea' | 'multiple-choice' | 'hybrid-style';
}
interface ButtonProps { 
    children: ReactNode; 
    className?: string; 
    onClick?: (e?: MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean; 
    variant?: 'primary' | 'ghost' | 'danger' | 'light'; 
    type?: 'button' | 'submit' | 'reset'; 
}
interface ConfirmationModalProps { isOpen: boolean; onClose: () => void; onConfirm: () => void; title: string; message: string; }
interface AiImageModalProps { isOpen: boolean; onClose: () => void; onGenerate: (prompt: string) => Promise<void>; isLoading: boolean; }
interface ToolbarProps { top: number; left: number; onRewrite?: (action: string) => void; onFormat?: (format: string) => void; }
interface AiImproveModalProps { isOpen: boolean; onClose: () => void; onConfirm: (prompt: string) => Promise<void>; isLoading: boolean; }


// --- Configuration de l'assistant de création (Onboarding) ---
const onboardingSteps: OnboardingStep[] = [
    { id: 'industry', question: "Dans quel secteur d'activité opérez-vous ?", placeholder: "Ex: Restaurant, consultant, photographe...", type: 'text' },
    { id: 'style', question: "Choisissez un point de départ ou décrivez le style que vous voulez.", type: 'hybrid-style',
        templates: [
            { name: 'Moderne & Épuré', description: 'Un design minimaliste avec beaucoup d\'espace blanc, une typographie sans-serif propre (comme Inter ou Manrope), une palette de couleurs neutres (noir, blanc, gris) avec une seule couleur d\'accentuation vive (ex: bleu électrique). Les animations sont subtiles et fluides.' },
            { name: 'Créatif & Coloré', description: 'Un design audacieux et vibrant utilisant des dégradés de couleurs, des formes géométriques ou organiques, et une typographie expressive. Des micro-interactions et des animations engageantes sont présentes pour surprendre l\'utilisateur.' },
            { name: 'Corporate & Élégant', description: 'Un style sobre et professionnel avec une mise en page structurée en grille. Utilise une palette de couleurs sérieuses (bleu marine, gris foncé) avec des touches de blanc. La typographie est classique et très lisible (ex: Lato, Merriweather). L\'accent est mis sur la confiance et la clarté.' },
            { name: 'Chaleureux & Artisanal', description: 'Un design chaleureux qui utilise des textures (comme le papier ou le bois en arrière-plan), des couleurs terreuses (beige, marron, vert olive) et une typographie script ou serif qui évoque l\'authenticité. Les icônes peuvent avoir un style dessiné à la main.' }
        ],
        placeholder: "Ou décrivez votre propre style ici... Ex: Un look sombre (dark mode) avec des accents néon verts, style tech/gaming."
    },
    { id: 'pages', question: "Quelles pages souhaitez-vous inclure ?",
        options: [ 'Accueil (index.html)', 'À Propos (a-propos.html)', 'Services (services.html)', 'Galerie (galerie.html)', 'Blog (blog.html)', 'Témoignages (temoignages.html)', 'Contact (contact.html)' ],
        type: 'multiple-choice'
    },
    { id: 'name', question: "Quel est le nom de votre entreprise ou projet ?", placeholder: "Ex: Les Délices de Marie", type: 'text' },
    { id: 'description', question: "Décrivez votre activité et ce que vous voulez que votre site transmette.", placeholder: "Ex: Nous sommes un café familial...", type: 'textarea' },
];

// --- Contextes (i18n, Auth) ---
const translations: Record<string, Record<string, string>> = {
    fr: {
        features: 'Fonctionnalités', howItWorks: 'Comment ça marche', pricing: 'Tarifs', login: 'Connexion', getStarted: 'Démarrer',
        heroTitle: "Votre site web, créé par l'IA en 2 minutes.", heroSubtitle: "Décrivez simplement votre projet. Notre intelligence artificielle s'occupe du design, des textes et de la mise en page pour un résultat professionnel et unique.",
        generateSite: "Générer mon site gratuitement", serverStatus: "Serveur", step1Title: "1. Décrivez", step1Desc: "Répondez à quelques questions sur votre activité et vos préférences de style.",
        step2Title: "2. Générez", step2Desc: "Notre IA conçoit une proposition unique de site web, textes et images inclus.", step3Title: "3. Personnalisez", step3Desc: "Ajustez les détails avec notre éditeur simple et publiez votre site en un clic.",
        feature1Title: "Génération par IA", feature1Desc: "Obtenez un site complet (design, textes, structure) à partir d'une simple description.", feature2Title: "Personnalisation Visuelle", feature2Desc: "Cliquez et modifiez directement votre site. L'IA peut même réécrire vos textes pour vous.",
        feature3Title: "Images par IA", feature3Desc: "Cliquez sur une image et décrivez ce que vous voulez pour la remplacer instantanément.", pricingTitle: "Un tarif simple et transparent",
        pricingDesc: "Tout ce dont vous avez besoin, sans frais cachés.", pricePerMonth: "/mois", pricingFeature1: "Sites illimités", pricingFeature2: "Générations IA illimitées",
        pricingFeature3: "Hébergement inclus", pricingFeature4: "Support client prioritaire", startYourTrial: "Devenir membre"
    },
};

const I18nContext = createContext<{t: (key: string) => string; setLocale: (locale: string) => void; locale: string;} | null>(null);
const I18nProvider: FC<{children: ReactNode}> = ({ children }) => {
    const [locale, setLocale] = useState('fr');
    const t = (key: string): string => (translations.fr as any)[key] || key;
    return <I18nContext.Provider value={{ t, setLocale, locale }}>{children}</I18nContext.Provider>;
}
const useTranslation = () => {
    const context = useContext(I18nContext);
    if (!context) throw new Error("useTranslation must be used within an I18nProvider");
    return context;
};

interface AuthContextType { user: UserData | null; token: string | null; isAuthenticated: boolean; login: (userData: UserData, token: string) => void; logout: () => void; }
const AuthContext = createContext<AuthContextType | null>(null);
const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};
const AuthProvider: FC<{children: ReactNode}> = ({ children }) => {
    const [user, setUser] = useState<UserData | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        if (storedToken && storedUser) {
            try {
                setUser(JSON.parse(storedUser));
                setToken(storedToken);
            } catch(e) {
                console.error("Failed to parse user data", e);
                logout();
            }
        }
    }, []);
    
    const login = (userData: UserData, userToken: string) => {
        localStorage.setItem('token', userToken);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setToken(userToken);
    };
    
    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
    };
    
    const isAuthenticated = !!token && !!user;

    return <AuthContext.Provider value={{ user, token, isAuthenticated, login, logout }}>{children}</AuthContext.Provider>;
};

// --- Composants Utilitaires ---
const Button: FC<ButtonProps> = ({ children, className, onClick, disabled, variant = 'primary', ...props }) => {
    const baseClasses = "px-4 py-2 font-semibold rounded-lg shadow-md transition-transform transform hover:scale-105 focus:outline-none focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2";
    const variants = { primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-300", ghost: "bg-transparent text-white hover:bg-gray-700", danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-300", light: "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400" };
    return <button className={`${baseClasses} ${variants[variant]} ${className}`} onClick={onClick} disabled={disabled} {...props}>{children}</button>
};
const ErrorMessage: FC<{ message: string, onRetry?: () => void }> = ({ message, onRetry }) => <div className="w-full max-w-md mx-auto text-center bg-red-900/50 border border-red-700 p-6 rounded-lg text-white"><AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" /><h3 className="text-xl font-bold mb-2">Erreur</h3><p className="mb-6">{message}</p>{onRetry && <Button onClick={onRetry}>Réessayer</Button>}</div>;

const ServerStatusIndicator: FC = () => {
    const { t } = useTranslation();
    const [status] = useState('online');
    return <div className={`text-sm flex items-center ${status === 'online' ? 'text-green-500' : 'text-red-500'}`}>{status === 'online' ? <Wifi className="h-4 w-4 mr-2" /> : <WifiOff className="h-4 w-4 mr-2" />} {t('serverStatus')} {status}</div>;
};

const ConfirmationModal: FC<ConfirmationModalProps> = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;
    return <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"><div className="bg-gray-800 p-8 rounded-lg shadow-xl max-w-sm w-full"><h3 className="text-xl font-bold text-white mb-4">{title}</h3><p className="text-gray-300 mb-6">{message}</p><div className="flex justify-end space-x-4"><Button onClick={onClose} variant="ghost">Annuler</Button><Button onClick={onConfirm} variant="danger">Confirmer</Button></div></div></div>;
};
const AiImageModal: FC<AiImageModalProps> = ({ isOpen, onClose, onGenerate, isLoading }) => {
    const [prompt, setPrompt] = useState('');
    if (!isOpen) return null;
    return <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"><div className="bg-gray-800 p-8 rounded-lg shadow-xl max-w-md w-full text-white"><h3 className="text-xl font-bold mb-4">Générer une Image par IA</h3><p className="text-gray-400 mb-4">Décrivez l'image que vous souhaitez créer.</p><textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Ex: Un logo moderne pour un café, avec une tasse de café stylisée" rows={3} className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg mb-4" /> <div className="flex justify-end gap-4"><Button onClick={onClose} variant="ghost">Annuler</Button><Button onClick={() => onGenerate(prompt)} disabled={isLoading || !prompt}>{isLoading ? 'Génération...' : 'Générer'}</Button></div></div></div>
};
const AiImproveModal: FC<AiImproveModalProps> = ({ isOpen, onClose, onConfirm, isLoading }) => {
    const [prompt, setPrompt] = useState('');
    if (!isOpen) return null;
    return <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]"><div className="bg-gray-800 p-8 rounded-lg shadow-xl max-w-md w-full text-white"><h3 className="text-xl font-bold mb-4">Améliorer la section avec l'IA</h3><p className="text-gray-400 mb-4">Décrivez les améliorations souhaitées.</p><textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Ex: Rends cette section plus moderne et ajoute un bouton d'appel à l'action." rows={3} className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg mb-4" /> <div className="flex justify-end gap-4"><Button onClick={onClose} variant="ghost">Annuler</Button><Button onClick={() => onConfirm(prompt)} disabled={isLoading || !prompt}>{isLoading ? 'Amélioration...' : 'Lancer l\'IA'}</Button></div></div></div>
};
const EditingToolbar: FC<ToolbarProps> = ({ top, left, onRewrite, onFormat }) => {
    const handleMouseDown = (e: MouseEvent<HTMLButtonElement>, format: string) => {
        e.preventDefault(); // Prevent loss of selection
        if (onFormat) onFormat(format);
    };

    return (
        <div className="absolute z-[1001] bg-gray-900 text-white rounded-lg shadow-2xl flex items-center divide-x divide-gray-700" style={{ top, left }}>
            <div className="flex items-center p-1 gap-1">
                <button onMouseDown={(e) => handleMouseDown(e, 'bold')} title="Gras" className="p-2 hover:bg-gray-700 rounded"><Bold size={16}/></button>
                <button onMouseDown={(e) => handleMouseDown(e, 'italic')} title="Italique" className="p-2 hover:bg-gray-700 rounded"><Italic size={16}/></button>
                <button onMouseDown={(e) => handleMouseDown(e, 'underline')} title="Souligné" className="p-2 hover:bg-gray-700 rounded"><Underline size={16}/></button>
                <button onMouseDown={(e) => handleMouseDown(e, 'insertUnorderedList')} title="Liste à puces" className="p-2 hover:bg-gray-700 rounded"><List size={16}/></button>
                <button onMouseDown={(e) => handleMouseDown(e, 'insertOrderedList')} title="Liste numérotée" className="p-2 hover:bg-gray-700 rounded"><ListOrdered size={16}/></button>
            </div>
            {onRewrite && (
                 <div className="flex items-center p-1 gap-1">
                    <button onClick={() => onRewrite('improve')} className="px-3 py-2 text-sm hover:bg-gray-700 rounded text-blue-400 flex items-center gap-1"><Sparkles size={14}/> Améliorer</button>
                    <button onClick={() => onRewrite('shorter')} className="px-3 py-2 text-sm hover:bg-gray-700 rounded">Raccourcir</button>
                </div>
            )}
        </div>
    );
};

// --- Composants d'Authentification ---
const LoginPage: FC<{ onRegisterClick: () => void }> = ({ onRegisterClick }) => {
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || "Identifiants invalides");
            login(data.user, data.token);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="text-white text-center flex flex-col justify-center items-center h-full p-4">
            <h2 className="text-3xl font-bold mb-4">Connexion</h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-sm mx-auto w-full">
                <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="p-3 rounded bg-gray-800 border border-gray-700" required />
                <input type="password" placeholder="Mot de passe" value={password} onChange={e => setPassword(e.target.value)} className="p-3 rounded bg-gray-800 border border-gray-700" required />
                {error && <p className="text-red-500">{error}</p>}
                <Button type="submit" disabled={loading}>{loading ? "Connexion..." : "Se connecter"}</Button>
                <p className="text-sm">Pas encore de compte ? <button type="button" onClick={onRegisterClick} className="text-blue-400 hover:underline">Inscrivez-vous</button></p>
            </form>
        </div>
    );
};
const RegisterPage: FC<{ onLoginClick: () => void }> = ({ onLoginClick }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Une erreur est survenue.');
            setSuccess('Compte créé avec succès ! Vous pouvez maintenant vous connecter.');
            setTimeout(() => onLoginClick(), 2000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="text-white text-center flex flex-col justify-center items-center h-full p-4">
            <h2 className="text-3xl font-bold mb-4">Inscription</h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-sm mx-auto w-full">
                <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="p-3 rounded bg-gray-800 border border-gray-700" required />
                <input type="password" placeholder="Mot de passe" value={password} onChange={e => setPassword(e.target.value)} className="p-3 rounded bg-gray-800 border border-gray-700" required />
                {error && <p className="text-red-500">{error}</p>}
                {success && <p className="text-green-500">{success}</p>}
                <Button type="submit" disabled={loading}>{loading ? "Inscription..." : "S'inscrire"}</Button>
                <p className="text-sm">Déjà un compte ? <button type="button" onClick={onLoginClick} className="text-blue-400 hover:underline">Connectez-vous</button></p>
            </form>
        </div>
    );
};

// --- Composants Principaux ---
const LandingPage: FC<{ onLoginClick: () => void; onRegisterClick: () => void; }> = ({ onLoginClick, onRegisterClick }) => {
    const { t } = useTranslation();
    const { isAuthenticated } = useAuth();

    return (
        <div className="bg-gray-50 text-gray-800 font-sans overflow-y-auto h-screen">
            <header className="container mx-auto px-6 py-4 flex justify-between items-center sticky top-0 bg-white/80 backdrop-blur-md z-10">
                <h1 className="text-2xl font-bold text-gray-900">{APP_NAME}</h1>
                <nav className="space-x-4 hidden md:flex items-center">
                    <a href="#how-it-works" className="text-gray-600 hover:text-blue-600 transition-colors">{t('howItWorks')}</a>
                    <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors">{t('features')}</a>
                    <a href="#pricing" className="text-gray-600 hover:text-blue-600 transition-colors">{t('pricing')}</a>
                    <Button onClick={onLoginClick} variant="light">{t('login')}</Button>
                    <Button onClick={onRegisterClick}>{t('getStarted')}</Button>
                </nav>
                <div className="md:hidden"> <Button onClick={onLoginClick}>{t('login')}</Button> </div>
            </header>
            <main>
                <section className="container mx-auto px-6 text-center pt-24 pb-16">
                    <h2 className="text-5xl md:text-7xl font-extrabold leading-tight mb-4 text-gray-900">
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-blue-600">{t('heroTitle')}</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">{t('heroSubtitle')}</p>
                    <div className="flex flex-col items-center space-y-4">
                        <Button onClick={onRegisterClick} className="text-lg">{t('generateSite')} <ArrowRight className="inline ml-2" /></Button>
                        <ServerStatusIndicator />
                    </div>
                </section>

                 <section id="how-it-works" className="py-20 bg-white">
                    <div className="container mx-auto px-6 text-center">
                        <h3 className="text-4xl font-bold mb-12">{t('howItWorks')}</h3>
                        <div className="grid md:grid-cols-3 gap-12">
                            <div className="flex flex-col items-center"><div className="bg-blue-100 text-blue-600 rounded-full p-4 mb-4"><Palette size={32}/></div><h4 className="text-xl font-bold mb-2">{t('step1Title')}</h4><p className="text-gray-600">{t('step1Desc')}</p></div>
                            <div className="flex flex-col items-center"><div className="bg-blue-100 text-blue-600 rounded-full p-4 mb-4"><Cpu size={32}/></div><h4 className="text-xl font-bold mb-2">{t('step2Title')}</h4><p className="text-gray-600">{t('step2Desc')}</p></div>
                            <div className="flex flex-col items-center"><div className="bg-blue-100 text-blue-600 rounded-full p-4 mb-4"><Sparkles size={32}/></div><h4 className="text-xl font-bold mb-2">{t('step3Title')}</h4><p className="text-gray-600">{t('step3Desc')}</p></div>
                        </div>
                    </div>
                </section>

                <section id="features" className="py-20">
                    <div className="container mx-auto px-6 grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-lg shadow-md"><div className="text-blue-600 mb-4"><Cpu size={40}/></div><h4 className="text-2xl font-bold mb-3">{t('feature1Title')}</h4><p>{t('feature1Desc')}</p></div>
                        <div className="bg-white p-8 rounded-lg shadow-md"><div className="text-blue-600 mb-4"><Brush size={40}/></div><h4 className="text-2xl font-bold mb-3">{t('feature2Title')}</h4><p>{t('feature2Desc')}</p></div>
                        <div className="bg-white p-8 rounded-lg shadow-md"><div className="text-blue-600 mb-4"><Edit size={40}/></div><h4 className="text-2xl font-bold mb-3">{t('feature3Title')}</h4><p>{t('feature3Desc')}</p></div>
                    </div>
                </section>

                <section id="pricing" className="py-20 bg-white">
                    <div className="container mx-auto px-6 text-center">
                        <h3 className="text-4xl font-bold mb-4">{t('pricingTitle')}</h3>
                        <p className="text-lg text-gray-600 max-w-xl mx-auto mb-12">{t('pricingDesc')}</p>
                        <div className="bg-white rounded-xl shadow-lg p-10 inline-block max-w-sm border">
                            <p className="text-5xl font-extrabold">{PRICE.split('/')[0]}</p>
                            <p className="text-gray-500 mb-6">{t('pricePerMonth')}</p>
                            <ul className="space-y-4 text-left mb-8">
                                <li className="flex items-center"><CheckCircle className="text-green-500 mr-3"/>{t('pricingFeature1')}</li>
                                <li className="flex items-center"><CheckCircle className="text-green-500 mr-3"/>{t('pricingFeature2')}</li>
                                <li className="flex items-center"><CheckCircle className="text-green-500 mr-3"/>{t('pricingFeature3')}</li>
                                <li className="flex items-center"><CheckCircle className="text-green-500 mr-3"/>{t('pricingFeature4')}</li>
                            </ul>
                            <Button onClick={isAuthenticated ? () => {} : onRegisterClick} className="w-full text-lg">{t('startYourTrial')}</Button>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

const SiteEditor: FC<{ initialSiteData: Site; onBack: () => void }> = ({ initialSiteData, onBack }) => {
    const { token } = useAuth();
    const [site, setSite] = useState<Site>(initialSiteData);
    const [activeFile, setActiveFile] = useState<string>('');
    const [previewContent, setPreviewContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [editorMode, setEditorMode] = useState<'visual' | 'code'>('visual');
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // --- State for modals and toolbar ---
    const [imageModal, setImageModal] = useState({ isOpen: false, targetId: null as string | null, isLoading: false });
    const [aiImproveModal, setAiImproveModal] = useState({ isOpen: false, isLoading: false, targetElement: null as HTMLElement | null });
    const [editingToolbar, setEditingToolbar] = useState<{top: number, left: number} | null>(null);
    const [selectedTextElement, setSelectedTextElement] = useState<HTMLElement | null>(null);

    // --- Effect to set the initial active file ---
    useEffect(() => {
        if (site.content && typeof site.content !== 'string' && site.content.files.length > 0) {
            const indexFile = site.content.files.find(f => f.name.includes('index.html'))?.name;
            setActiveFile(indexFile || site.content.files[0].name);
        }
    }, [site.content]); // Runs only when site content changes

    // --- Effect to build the preview content whenever the active file or site content changes ---
    useEffect(() => {
        if (typeof site.content === 'string' || !site.content.files) return;

        const htmlFile = site.content.files.find(f => f.name === activeFile);
        const cssFile = site.content.files.find(f => f.name.endsWith('.css'));

        if (htmlFile) {
            const finalHtml = `
                <html>
                    <head>
                        <style>${cssFile?.content || ''}</style>
                        <style>
                            /* Styles for visual editor helpers */
                            [data-editable="true"]:hover { background-color: rgba(0, 123, 255, 0.1); cursor: text; outline: 1px dashed rgba(0, 123, 255, 0.5); }
                            [data-draggable-section="true"] { position: relative; }
                            .section-handle-container { position: absolute; top: 8px; left: 8px; z-index: 1000; background: rgba(17, 24, 39, 0.9); border-radius: 6px; padding: 4px; display: flex; gap: 4px; border: 1px solid #4b5563; opacity: 0; transition: opacity 0.2s ease-in-out; user-select: none; }
                            [data-draggable-section="true"]:hover > .section-handle-container { opacity: 1; }
                            .section-handle-btn { cursor: pointer; background: none; border: none; padding: 4px; line-height: 0; color: white; }
                            .section-handle-move { cursor: move; }
                            .drop-indicator { height: 4px; background: #3b82f6; margin: 8px 0; border-radius: 2px; list-style: none; }
                        </style>
                    </head>
                    <body>
                        ${htmlFile.content}
                    </body>
                </html>`;
            setPreviewContent(finalHtml);
        }
    }, [site.content, activeFile]);

    const updateSourceCodeFromIframe = useCallback(() => {
        const iframeDoc = iframeRef.current?.contentDocument;
        if (!iframeDoc || typeof site.content === 'string') return;
        
        // Clone the body and clean it up before saving
        const bodyClone = iframeDoc.body.cloneNode(true) as HTMLElement;
        bodyClone.querySelectorAll('.section-handle-container, .drop-indicator, [data-editor-temp]').forEach(el => el.remove());
        
        const newHtmlContent = bodyClone.innerHTML;

        const updatedFiles = (site.content as SiteContent).files.map(file =>
            file.name === activeFile ? { ...file, content: newHtmlContent } : file
        );
        setSite(prev => ({ ...prev, content: { files: updatedFiles } }));
    }, [activeFile, site.content]);

    // --- THE MAIN EFFECT FOR VISUAL EDITOR ---
    // This effect runs when the editor mode switches to 'visual'.
    // It sets up a 'load' listener on the iframe. All visual editor logic is inside handleLoad.
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || editorMode !== 'visual') return;

        const handleLoad = () => {
            const iframeDoc = iframe.contentDocument;
            if (!iframeDoc) return;
            
            // --- 3. Synchronized Multi-page Management ---
            iframeDoc.body.addEventListener('click', (e: Event) => {
                const target = e.target as HTMLElement;
                const anchor = target.closest('a');
                if (anchor && anchor.hasAttribute('href')) {
                    e.preventDefault();
                    const href = anchor.getAttribute('href');
                    if (href && (site.content as SiteContent).files.some(f => f.name === href)) {
                        setActiveFile(href);
                    }
                }
            });

            // --- Make text elements editable ---
            iframeDoc.querySelectorAll<HTMLElement>('[data-editable="true"]').forEach(el => {
                el.contentEditable = 'true';
                el.addEventListener('blur', updateSourceCodeFromIframe);
            });
            
            // --- Make images clickable for AI generation ---
            iframeDoc.querySelectorAll<HTMLImageElement>('img[data-img-id]').forEach(img => {
                img.style.cursor = 'pointer';
                img.addEventListener('click', () => {
                    const imgId = img.getAttribute('data-img-id');
                    setImageModal({ isOpen: true, targetId: imgId, isLoading: false });
                });
            });

            // --- 1. "Word-style" Editing Toolbar ---
            iframeDoc.addEventListener('selectionchange', () => {
                const selection = iframeDoc.getSelection();
                if (selection && !selection.isCollapsed) {
                    const range = selection.getRangeAt(0);
                    const parent = range.commonAncestorContainer.parentElement;
                    if (parent && parent.closest('[data-editable="true"]') && iframeRef.current) {
                        const rect = range.getBoundingClientRect();
                        const iframeRect = iframeRef.current.getBoundingClientRect();
                        setSelectedTextElement(parent);
                        setEditingToolbar({ 
                            top: iframeRect.top + rect.top - 50, // Position above the selection
                            left: iframeRect.left + rect.left + rect.width / 2 - 80 // Center the toolbar
                        });
                    }
                } else {
                    setEditingToolbar(null);
                }
            });

            // --- 2. Draggable Sections ---
            let draggedElement: HTMLElement | null = null;
            const dropIndicator = iframeDoc.createElement('div');
            dropIndicator.className = 'drop-indicator';

            iframeDoc.querySelectorAll<HTMLElement>('[data-draggable-section="true"]').forEach(section => {
                if (section.querySelector('.section-handle-container')) return;

                const handleContainer = iframeDoc.createElement('div');
                handleContainer.className = 'section-handle-container';
                handleContainer.setAttribute('data-editor-temp', 'true');
                
                const moveHandle = iframeDoc.createElement('button');
                moveHandle.className = 'section-handle-btn section-handle-move';
                moveHandle.title = "Déplacer";
                moveHandle.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/></svg>`;
                moveHandle.draggable = true;

                const aiHandle = iframeDoc.createElement('button');
                aiHandle.className = 'section-handle-btn';
                aiHandle.title = "Améliorer avec l'IA";
                aiHandle.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 2.5a2.5 2.5 0 0 1 5 0"/><path d="M2.5 9.5a2.5 2.5 0 0 1 0-5"/><path d="M16.5 21.5a2.5 2.5 0 0 1 0-5"/><path d="M21.5 9.5a2.5 2.5 0 0 0-5 0"/><path d="M4.5 4.5 9 9"/><path d="m15 15 4.5 4.5"/><path d="m9 15 6-6"/></svg>`;
                
                aiHandle.onclick = (e) => { 
                    e.stopPropagation(); 
                    e.preventDefault();
                    setAiImproveModal({ isOpen: true, isLoading: false, targetElement: section });
                };

                handleContainer.appendChild(moveHandle);
                handleContainer.appendChild(aiHandle);
                section.prepend(handleContainer);

                moveHandle.addEventListener('dragstart', (e) => {
                    e.stopPropagation();
                    draggedElement = section;
                    if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', '');
                    }
                    setTimeout(() => section.style.opacity = '0.5', 0);
                });

                section.addEventListener('dragend', () => {
                    section.style.opacity = '1';
                });
            });

            iframeDoc.body.addEventListener('dragover', (e: DragEvent) => {
                e.preventDefault();
                if (!draggedElement) return;

                const targetSection = (e.target as HTMLElement).closest('[data-draggable-section="true"]');
                if (targetSection && targetSection !== draggedElement) {
                    const rect = targetSection.getBoundingClientRect();
                    const isAfter = e.clientY > rect.top + rect.height / 2;
                    if (isAfter) {
                        targetSection.parentNode?.insertBefore(dropIndicator, targetSection.nextSibling);
                    } else {
                        targetSection.parentNode?.insertBefore(dropIndicator, targetSection);
                    }
                }
            });

            iframeDoc.body.addEventListener('drop', (e: DragEvent) => {
                e.preventDefault();
                if (draggedElement && dropIndicator.parentNode) {
                    dropIndicator.parentNode.insertBefore(draggedElement, dropIndicator);
                    updateSourceCodeFromIframe();
                }
                draggedElement = null;
                dropIndicator.remove();
            });
        };

        iframe.addEventListener('load', handleLoad);
        
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
            handleLoad();
        }

        return () => {
            iframe.removeEventListener('load', handleLoad);
        };
    }, [editorMode, previewContent, site.content, updateSourceCodeFromIframe]);
    
    // --- Handlers for modals and actions ---
    const handleRichTextFormat = (format: string) => {
        iframeRef.current?.contentDocument?.execCommand(format, false);
        iframeRef.current?.focus();
    };

    const handleAiRewrite = async (action: string) => {
        if (!selectedTextElement || !token) return;
        const originalText = selectedTextElement.innerText;
        setEditingToolbar(null);
        selectedTextElement.innerText = "Génération...";
        try {
            const response = await fetch(`${API_BASE_URL}/api/ai/rewrite-text`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ text: originalText, tone: action })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error);
            selectedTextElement.innerHTML = data.rewrittenText;
            updateSourceCodeFromIframe();
        } catch (error) {
            console.error("Rewrite error:", error);
            selectedTextElement.innerText = originalText;
        }
    };

    const handleAiImageGenerate = async (prompt: string) => {
        if (!imageModal.targetId || !token) return;
        setImageModal(prev => ({ ...prev, isLoading: true }));
        try {
            const response = await fetch(`${API_BASE_URL}/api/ai/generate-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ prompt })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error);
            
            const imageUrl = `data:image/png;base64,${data.base64Image}`;
            
            const iframeImg = iframeRef.current?.contentDocument?.querySelector<HTMLImageElement>(`img[data-img-id="${imageModal.targetId}"]`);
            if (iframeImg) {
                iframeImg.src = imageUrl;
            }
            updateSourceCodeFromIframe();

        } catch (error) {
            console.error("Image generation error:", error);
        } finally {
            setImageModal({ isOpen: false, targetId: null, isLoading: false });
        }
    };
    
    const handleAiImprove = async (userPrompt: string) => {
        const { targetElement } = aiImproveModal;
        if (!targetElement || !token) return;
        setAiImproveModal(prev => ({ ...prev, isLoading: true }));

        try {
            const tempDiv = targetElement.cloneNode(true) as HTMLElement;
            tempDiv.querySelector('.section-handle-container')?.remove();

            const response = await fetch(`${API_BASE_URL}/api/ai/improve-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ htmlContent: tempDiv.outerHTML, userPrompt })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error);
            
            targetElement.outerHTML = data.improvedCode;
            updateSourceCodeFromIframe();

        } catch (error) {
            console.error("AI Improve error:", error);
        } finally {
            setAiImproveModal({ isOpen: false, isLoading: false, targetElement: null });
        }
    };

    const handleSave = async () => {
        if (editorMode === 'visual') {
            updateSourceCodeFromIframe();
        }
        if (!token) return;
        setIsSaving(true);
        setSaveStatus('idle');
        try {
            const contentToSave = site.content;
            const response = await fetch(`${API_BASE_URL}/api/sites/${site.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ content: contentToSave }),
            });
            if (!response.ok) throw new Error("La sauvegarde a échoué");
            setSaveStatus('success');
        } catch (error) {
            console.error("Save error:", error);
            setSaveStatus('error');
        } finally {
            setIsSaving(false);
            setTimeout(() => setSaveStatus('idle'), 2000);
        }
    };
    
    const handleDownloadZip = () => {
        if (editorMode === 'visual') {
            updateSourceCodeFromIframe();
        }
        const zip = new JSZip();
        if (typeof site.content !== 'string' && site.content.files) {
            site.content.files.forEach(file => {
                zip.file(file.name, file.content);
            });
            zip.generateAsync({ type: "blob" }).then(content => {
                saveAs(content, `${site.name.replace(/\s+/g, '_')}.zip`);
            });
        }
    };
    
    const handleCodeChange = (newContent: string) => {
        if (typeof site.content === 'string') return;
        const updatedFiles = site.content.files.map(file =>
            file.name === activeFile ? { ...file, content: newContent } : file
        );
        setSite({ ...site, content: { files: updatedFiles } });
    };

    const getLanguage = (filename: string) => {
        const extension = filename.split('.').pop();
        if (extension === 'js') return Prism.languages.js;
        if (extension === 'css') return Prism.languages.css;
        if (extension === 'html') return Prism.languages.markup;
        return Prism.languages.clike;
    };
    const currentFileContent = (typeof site.content !== 'string' && site.content.files.find(f => f.name === activeFile)?.content) || '';

    return (
        <div className="w-full h-screen bg-gray-900 text-white flex flex-col font-sans">
            <AiImageModal isOpen={imageModal.isOpen} onClose={() => setImageModal({isOpen: false, targetId: null, isLoading: false})} onGenerate={handleAiImageGenerate} isLoading={imageModal.isLoading} />
            <AiImproveModal isOpen={aiImproveModal.isOpen} onClose={() => setAiImproveModal({ isOpen: false, isLoading: false, targetElement: null })} onConfirm={handleAiImprove} isLoading={aiImproveModal.isLoading} />
            {editingToolbar && <EditingToolbar top={editingToolbar.top} left={editingToolbar.left} onRewrite={handleAiRewrite} onFormat={handleRichTextFormat} />}
            
            <header className="flex-shrink-0 bg-gray-800 border-b border-gray-700 p-2 flex justify-between items-center z-20">
                <div className="flex items-center gap-4">
                    <Button onClick={onBack} variant="ghost"><ArrowLeft size={18} /> Retour</Button>
                    <h1 className="text-lg font-bold">{site.name}</h1>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={handleDownloadZip} variant="light"><Download size={16}/> .zip</Button>
                    <Button onClick={handleSave} disabled={isSaving}>{isSaving ? 'Sauvegarde...' : <><Save size={16}/> Sauvegarder</>}</Button>
                    {saveStatus === 'success' && <CheckCircle className="text-green-500" />}
                    {saveStatus === 'error' && <AlertTriangle className="text-red-500" />}
                </div>
            </header>

            <main className="flex-grow flex min-h-0">
                <aside className="w-64 bg-gray-800/50 p-4 border-r border-gray-700 flex flex-col flex-shrink-0">
                    <h2 className="text-lg font-bold mb-4">Pages & Fichiers</h2>
                    <div className="flex-grow overflow-y-auto">
                        {typeof site.content !== 'string' && site.content.files.map(file => (
                            <button key={file.name} onClick={() => setActiveFile(file.name)} className={`w-full text-left px-3 py-2 my-1 rounded-md flex items-center gap-2 transition-colors ${activeFile === file.name ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'}`}>
                                {file.name.endsWith('.css') ? <Palette size={16}/> : <FileCode size={16}/>}
                                <span className="truncate">{file.name}</span>
                            </button>
                        ))}
                    </div>
                </aside>

                <div className="flex-grow flex flex-col">
                    <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 p-2 flex justify-center">
                        <div className="bg-gray-700 p-1 rounded-lg flex gap-1">
                            <button onClick={() => setEditorMode('visual')} className={`px-3 py-1 rounded-md text-sm flex items-center gap-2 ${editorMode === 'visual' ? 'bg-blue-600' : 'hover:bg-gray-600'}`}><Brush size={16}/> Visuel</button>
                            <button onClick={() => setEditorMode('code')} className={`px-3 py-1 rounded-md text-sm flex items-center gap-2 ${editorMode === 'code' ? 'bg-blue-600' : 'hover:bg-gray-600'}`}><Code size={16}/> Code</button>
                        </div>
                    </div>
                    {editorMode === 'code' ? (
                        <div className="flex-grow relative bg-[#282c34]">
                            <Editor value={currentFileContent} onValueChange={handleCodeChange} highlight={code => Prism.highlight(code, getLanguage(activeFile), activeFile.split('.').pop() || 'clike')} padding={10} className="h-full w-full absolute inset-0 font-mono text-base overflow-auto" style={{ fontFamily: '"Fira code", "Fira Mono", monospace', fontSize: 14 }} />
                        </div>
                    ) : (
                        <div className="flex-grow bg-white">
                            <iframe ref={iframeRef} srcDoc={previewContent} title="Live Preview" className="w-full h-full border-0" sandbox="allow-scripts allow-same-origin allow-popups" />
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

const OnboardingWizard: FC<{ onComplete: (data: Site) => void; onError: (msg: string) => void }> = ({ onComplete, onError }) => {
    const { token } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState<any>({ pages: ['Accueil (index.html)'] });
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("Analyse de vos réponses...");

    const handleInputChange = (id: string, value: any) => {
        setAnswers({ ...answers, [id]: value });
    };

    const handleTemplateClick = (description: string) => {
        setAnswers({ ...answers, style: description });
    };

    const handleMultipleChoiceClick = (choice: string) => {
        const currentChoices = answers[onboardingSteps[currentStep].id] || [];
        const newChoices = currentChoices.includes(choice) ? currentChoices.filter((c: string) => c !== choice) : [...currentChoices, choice];
        setAnswers({ ...answers, [onboardingSteps[currentStep].id]: newChoices });
    };
    
    const goToNextStep = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (currentStep < onboardingSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleSubmit(answers);
        }
    };
    
    const handleSubmit = async (finalAnswers: any) => {
        if (!token) return onError("Vous n'êtes pas authentifié.");
        setIsLoading(true);
        setLoadingText("Génération des fichiers HTML et CSS...");
        setTimeout(() => setLoadingText("Création du design..."), 3000);
        setTimeout(() => setLoadingText("Assemblage de votre site..."), 6000);
        try {
            const response = await fetch(`${API_BASE_URL}/api/sites/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ onboardingData: finalAnswers }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: "Une erreur inconnue est survenue" }));
                throw new Error(errorData.error);
            }
            const siteData = await response.json();
            onComplete(siteData);
        } catch (error: any) {
            onError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const step = onboardingSteps[currentStep];

    if (isLoading) return <div className="text-white text-center flex flex-col items-center justify-center h-full"><div className="bg-gray-800 p-10 rounded-xl shadow-2xl"><Cpu className="animate-pulse h-16 w-16 mx-auto text-blue-400" /><p className="mt-4 text-lg font-semibold">{loadingText}</p></div></div>;
    
    const renderStepContent = () => {
        switch (step.type) {
            case 'text':
                return (
                    <form onSubmit={goToNextStep}>
                        <input type="text" value={answers[step.id] || ''} onChange={(e) => handleInputChange(step.id, e.target.value)} placeholder={step.placeholder} className="w-full p-4 bg-gray-700 border border-gray-600 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:outline-none" autoFocus />
                        <Button type="submit" className="mt-6" disabled={!answers[step.id]}>Suivant <ArrowRight className="inline ml-2" /></Button>
                    </form>
                );
            case 'textarea':
                 return (
                    <form onSubmit={goToNextStep}>
                        <textarea value={answers[step.id] || ''} onChange={(e) => handleInputChange(step.id, e.target.value)} placeholder={step.placeholder} rows={5} className="w-full p-4 bg-gray-700 border border-gray-600 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:outline-none" autoFocus />
                        <Button type="submit" className="mt-6" disabled={!answers[step.id]}>
                            {currentStep === onboardingSteps.length - 1 ? 'Générer le site' : 'Suivant'}
                            {currentStep === onboardingSteps.length - 1 ? <Sparkles className="inline ml-2" /> : <ArrowRight className="inline ml-2" />}
                        </Button>
                    </form>
                );
            case 'multiple-choice':
                return (
                    <div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {step.options && step.options.map(option => {
                                const isSelected = (answers[step.id] || []).includes(option);
                                return <button key={option} onClick={() => handleMultipleChoiceClick(option)} className={`p-4 rounded-lg text-center transition-all duration-200 border-2 ${isSelected ? 'bg-blue-600 border-blue-500' : 'bg-gray-800 border-gray-700 hover:border-gray-500'}`}>
                                    <span className="font-semibold">{option.split(' ')[0]}</span>
                                </button>;
                            })}
                        </div>
                        <Button onClick={() => goToNextStep()} className="mt-8" disabled={!(answers[step.id] && answers[step.id].length > 0)}>Suivant <ArrowRight className="inline ml-2" /></Button>
                    </div>
                );
            case 'hybrid-style':
                return (
                     <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {step.templates?.map(template => (
                                <button key={template.name} onClick={() => handleTemplateClick(template.description)} className={`p-4 text-left rounded-lg transition-all duration-200 border-2 ${answers.style === template.description ? 'bg-blue-600/50 border-blue-500' : 'bg-gray-800 border-gray-700 hover:border-gray-500'}`}>
                                    <span className="text-lg font-semibold">{template.name}</span>
                                </button>
                            ))}
                        </div>
                        <textarea 
                            value={answers[step.id] || ''} 
                            onChange={(e) => handleInputChange(step.id, e.target.value)}
                            placeholder={step.placeholder} 
                            rows={4} 
                            className="w-full p-4 bg-gray-700 border border-gray-600 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                        />
                        <Button onClick={() => goToNextStep()} className="mt-6" disabled={!answers[step.id]}>Suivant <ArrowRight className="inline ml-2" /></Button>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="text-white flex flex-col justify-center items-center h-full">
            <div className="w-full max-w-3xl mx-auto px-4">
                <div className="mb-8 text-center">
                    <p className="text-blue-400 font-semibold">Étape {currentStep + 1} sur {onboardingSteps.length}</p>
                    <h2 className="text-3xl font-bold mt-1">{step.question}</h2>
                </div>
                {renderStepContent()}
            </div>
        </div>
    );
};

const Dashboard: FC<{ onStart: () => void; onAdminClick: () => void; onEditSite: (site: Site) => void; }> = ({ onStart, onAdminClick, onEditSite }) => {
    const { user, logout, token } = useAuth();
    const [sites, setSites] = useState<Site[]>([]);
    const [loading, setLoading] = useState(true);
    const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);

    useEffect(() => {
        const fetchSites = async () => {
            if (!token) return;
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/api/sites`, { headers: { 'Authorization': `Bearer ${token}` } });
                const data: Site[] = await response.json();
                if (response.ok) setSites(data);
            } catch (error) { console.error("Failed to fetch sites", error); }
            finally { setLoading(false); }
        };
        fetchSites();
    }, [token]);

    const handleDeleteSite = async () => {
        if (!siteToDelete || !token) return;
        try {
            await fetch(`${API_BASE_URL}/api/sites/${siteToDelete.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            setSites(sites.filter(site => site.id !== siteToDelete.id));
        } catch (error) { console.error("Error deleting site:", error); }
        finally { setSiteToDelete(null); }
    };

    const handleDownload = (site: Site) => {
        const zip = new JSZip();
        let contentToZip = site.content;

        // Handle case where content might be a stringified JSON
        if (typeof contentToZip === 'string') {
            try {
                contentToZip = JSON.parse(contentToZip);
            } catch (e) {
                console.error("Failed to parse site content, zipping as plain text.");
                zip.file("index.html", site.content as string);
                zip.generateAsync({ type: "blob" }).then(blob => saveAs(blob, `${site.name.replace(/\s+/g, '_')}.zip`));
                return;
            }
        }
        
        if (contentToZip && Array.isArray((contentToZip as SiteContent).files)) {
            (contentToZip as SiteContent).files.forEach(file => {
                zip.file(file.name, file.content);
            });
            zip.generateAsync({ type: "blob" }).then(blob => saveAs(blob, `${site.name.replace(/\s+/g, '_')}.zip`));
        } else {
            console.error("Invalid site content structure for zipping.");
        }
    };

    return (
        <>
            <ConfirmationModal isOpen={!!siteToDelete} onClose={() => setSiteToDelete(null)} onConfirm={handleDeleteSite} title="Supprimer le site" message={`Êtes-vous sûr de vouloir supprimer le site "${siteToDelete?.name}" ? Cette action est irréversible.`}/>
            <div className="w-full h-full bg-gray-800 text-white">
                <header className="p-4 bg-gray-900 flex justify-between items-center"><h1 className="text-xl font-bold">{APP_NAME} - Mon Espace</h1><div className="flex items-center space-x-4">{user?.role === 'ADMIN' && <Button onClick={onAdminClick} variant="ghost" className="flex items-center"><Shield className="mr-2 h-4 w-4"/> Admin</Button>}<span className="flex items-center"><User className="mr-2 h-4 w-4" /> {user?.email}</span><Button onClick={logout} variant="ghost">Déconnexion</Button></div></header>
                <main className="p-8">
                    <div className="flex justify-between items-center mb-8"><h2 className="text-3xl font-bold">Mes Sites</h2><Button onClick={onStart} className="flex items-center"><PlusCircle className="mr-2 h-5 w-5" /> Créer un nouveau site</Button></div>
                    {loading ? <p>Chargement de vos sites...</p> : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sites.map((site) => (
                                <div key={site.id} className="bg-gray-700 p-6 rounded-lg flex flex-col justify-between">
                                    <div><h3 className="text-xl font-bold mb-2">{site.name}</h3><p className="text-sm text-gray-400">Créé le: {new Date(site.createdAt).toLocaleDateString()}</p></div>
                                    <div className="mt-4 flex justify-end space-x-2">
                                        <button onClick={() => handleDownload(site)} title="Télécharger" className="p-2 text-gray-400 hover:text-blue-400 rounded-full hover:bg-gray-600 transition-colors"><Download size={18} /></button>
                                        <button onClick={() => onEditSite(site)} title="Modifier" className="p-2 text-gray-400 hover:text-green-400 rounded-full hover:bg-gray-600 transition-colors"><Edit size={18} /></button>
                                        <button onClick={() => setSiteToDelete(site)} title="Supprimer" className="p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-600 transition-colors"><Trash2 size={18} /></button>
                                    </div>
                                </div>
                            ))}
                            {sites.length === 0 && <p>Vous n'avez pas encore de site. Cliquez pour en créer un !</p>}
                        </div>
                    )}
                </main>
            </div>
        </>
    );
};

const AdminDashboard: FC<{ onDashboardClick: () => void }> = ({ onDashboardClick }) => {
    const { user, logout, token } = useAuth();
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!token) return;
            try {
                const [statsRes, usersRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch(`${API_BASE_URL}/api/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } })
                ]);
                const statsData = await statsRes.json();
                const usersData = await usersRes.json();
                if (statsRes.ok) setStats(statsData);
                if (usersRes.ok) setUsers(usersData);
            } catch (error) {
                console.error("Failed to fetch admin data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [token]);

    return (
        <div className="w-full h-full bg-gray-800 text-white">
            <header className="p-4 bg-gray-900 flex justify-between items-center">
                <h1 className="text-xl font-bold">{APP_NAME} - Panel Admin</h1>
                <div className="flex items-center space-x-4">
                    <Button onClick={onDashboardClick} variant="ghost" className="flex items-center"><LayoutDashboard className="mr-2 h-4 w-4"/> Mon Espace</Button>
                    <span className="flex items-center"><User className="mr-2 h-4 w-4" /> {user?.email}</span>
                    <Button onClick={logout} variant="ghost">Déconnexion</Button>
                </div>
            </header>
            <main className="p-8">
                {loading ? <p>Chargement des données...</p> : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <div className="bg-gray-700 p-6 rounded-lg"><h4 className="text-gray-400 text-sm">Utilisateurs</h4><p className="text-3xl font-bold">{stats?.userCount}</p></div>
                            <div className="bg-gray-700 p-6 rounded-lg"><h4 className="text-gray-400 text-sm">Sites Créés</h4><p className="text-3xl font-bold">{stats?.siteCount}</p></div>
                            <div className="bg-gray-700 p-6 rounded-lg"><h4 className="text-gray-400 text-sm">Abonnés Actifs</h4><p className="text-3xl font-bold">{stats?.subscriptionCount}</p></div>
                        </div>
                        <h2 className="text-2xl font-bold mb-4">Liste des Utilisateurs</h2>
                        <div className="bg-gray-700 rounded-lg overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-900"><tr><th className="p-4">Email</th><th className="p-4">Rôle</th><th className="p-4">Sites</th><th className="p-4">Abonné</th><th className="p-4">Inscrit le</th></tr></thead>
                                <tbody>
                                    {users.map(u => (
                                        <tr key={u.id} className="border-b border-gray-600"><td className="p-4">{u.email}</td><td className="p-4">{u.role}</td><td className="p-4">{u._count.sites}</td><td className="p-4">{u.stripeSubscriptionId ? <CheckCircle className="text-green-400"/> : ''}</td><td className="p-4">{new Date(u.createdAt).toLocaleDateString()}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

// --- Composant Principal de l'Application ---
export default function AppWrapper() {
    return (
        <I18nProvider>
            <AuthProvider>
                <App />
            </AuthProvider>
        </I18nProvider>
    );
}

function App() {
    const { isAuthenticated } = useAuth();
    const [appState, setAppState] = useState('loading');
    const [siteData, setSiteData] = useState<Site | null>(null);
    const [error, setError] = useState('');
    
    useEffect(() => {
        if (isAuthenticated) {
            setAppState('dashboard');
        } else {
            setAppState('landing');
        }
    }, [isAuthenticated]);

    const handleStart = () => setAppState('onboarding');
    const handleComplete = (data: Site) => { setSiteData(data); setAppState('editor'); };
    const handleError = (errorMessage: string) => { setError(errorMessage); setAppState('error'); };

    // --- FONCTION CORRIGÉE ---
    const handleEditSite = (site: Site) => {
        let siteToEdit = site;

        // Si le contenu est une chaîne, nous devons le normaliser.
        if (typeof site.content === 'string') {
            try {
                // Essayons d'abord de le parser comme du JSON (nouveau format).
                const parsedContent = JSON.parse(site.content);
                if (parsedContent && typeof parsedContent === 'object' && parsedContent.files) {
                    siteToEdit = { ...site, content: parsedContent };
                } else {
                    // C'est du JSON mais pas le bon format, on traite ça comme de l'ancien HTML.
                    throw new Error("Invalid JSON structure");
                }
            } catch (e) {
                // Si le parsing échoue, on suppose que c'est de l'ancien HTML brut.
                // On le convertit à la volée vers le nouveau format.
                console.log("Legacy content detected. Converting to new format.");
                const convertedContent: SiteContent = {
                    files: [{ name: 'index.html', content: site.content }]
                };
                siteToEdit = { ...site, content: convertedContent };
            }
        }
        
        // Si le contenu est déjà un objet mais n'a pas de propriété 'files' (improbable, mais sécurisant)
        else if (typeof site.content === 'object' && site.content !== null && !('files' in site.content)) {
            setError("Le format du site est invalide mais non convertible.");
            setAppState('error');
            return;
        }

        setSiteData(siteToEdit);
        setAppState('editor');
    };
    
    const handleBackToDashboard = () => {
        setSiteData(null);
        setAppState('dashboard');
    }

    const renderContent = () => {
        if (appState === 'loading') {
            return <div className="text-white">Chargement...</div>;
        }

        if (!isAuthenticated) {
            switch(appState) {
                case 'register': return <RegisterPage onLoginClick={() => setAppState('login')} />;
                case 'login': return <LoginPage onRegisterClick={() => setAppState('register')} />;
                default: return <LandingPage onLoginClick={() => setAppState('login')} onRegisterClick={() => setAppState('register')} />;
            }
        }

        // Authenticated routes
        switch(appState) {
            case 'onboarding': return <OnboardingWizard onComplete={handleComplete} onError={handleError} />;
            case 'editor': return siteData && <SiteEditor initialSiteData={siteData} onBack={handleBackToDashboard} />;
            case 'admin': return <AdminDashboard onDashboardClick={() => setAppState('dashboard')} />;
            case 'error': return <ErrorMessage message={error} onRetry={() => setAppState('dashboard')} />;
            case 'dashboard':
            default: 
                return <Dashboard onStart={handleStart} onAdminClick={() => setAppState('admin')} onEditSite={handleEditSite} />;
        }
    };

    return (
        <div className="bg-gray-900 min-h-screen flex items-center justify-center p-0 font-sans w-full h-screen">
            <div className="w-full h-full">{renderContent()}</div>
        </div>
    );
}
